import { Buffer } from 'buffer'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { OnchainKitProvider } from '@coinbase/onchainkit'
import { farcasterMiniApp } from '@farcaster/miniapp-wagmi-connector'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiProvider } from 'wagmi'
import { http, createConfig, fallback } from 'wagmi'
import { base, baseSepolia } from 'viem/chains'
import { injected, coinbaseWallet } from 'wagmi/connectors'
import sdk from '@farcaster/frame-sdk'
import App from './App'
import './index.css'

// Make Buffer available globally for browser compatibility
if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer
  // @ts-ignore
  globalThis.Buffer = globalThis.Buffer || Buffer
}

// Determine which network to use based on environment
const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'development'

// Select chain based on environment
const selectedChain = isDevelopment ? baseSepolia : base

// Get RPC URL for the selected chain
const rpcUrl = isDevelopment 
  ? (import.meta.env.VITE_BASE_SEPOLIA_RPC_URL || 'https://sepolia.base.org')
  : (import.meta.env.VITE_BASE_MAINNET_RPC_URL || 'https://mainnet.base.org')

// Create Wagmi config with injected connector first (for Farcaster), then Coinbase Wallet
// Use fallback transports with multiple RPC endpoints for better reliability
export const config = isDevelopment
  ? createConfig({
      chains: [baseSepolia],
      connectors: [
        farcasterMiniApp(),
        injected({ shimDisconnect: true }),
        coinbaseWallet({
          appName: 'BaseVibe',
          preference: 'smartWalletOnly',
        }),
      ],
      transports: {
        [baseSepolia.id]: fallback([
          http(rpcUrl, {
            retryCount: 3,
            retryDelay: 1000,
            timeout: 10_000,
          }),
          http('https://sepolia.base.org', {
            retryCount: 3,
            retryDelay: 1000,
            timeout: 10_000,
          }),
        ]),
      },
    })
  : createConfig({
      chains: [base],
      connectors: [
        farcasterMiniApp(),
        injected({ shimDisconnect: true }),
        coinbaseWallet({
          appName: 'BaseVibe',
          preference: 'smartWalletOnly',
        }),
      ],
      transports: {
        [base.id]: fallback([
          http(rpcUrl, {
            retryCount: 3,
            retryDelay: 1000,
            timeout: 10_000,
          }),
          http('https://mainnet.base.org', {
            retryCount: 3,
            retryDelay: 1000,
            timeout: 10_000,
          }),
        ]),
      },
    })

// Create React Query client with retry logic for better reliability
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <OnchainKitProvider
          apiKey={import.meta.env.VITE_ONCHAINKIT_API_KEY || ''}
          chain={selectedChain}
          miniKit={{
            enabled: true,
          }}
          config={{
            appearance: {
              mode: 'auto',
            },
          }}
        >
          <App />
        </OnchainKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  </React.StrictMode>,
)

// Signal to Farcaster that the miniapp is ready after mounting
if (typeof window !== 'undefined') {
  sdk.actions.ready()
}

